import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFailureMatrix } from '../../../hooks/useFailureMatrix';
import { useFailureFamilyDetail } from '../../../hooks/useFailureFamilyDetail';
import { useFailureCoverage } from '../../../hooks/useFailureCoverage';
import { CollapsibleNote } from '../CollapsibleNote';
import type { FailureDimension, FailureFamilyTotal, FailureMatrixResponse } from '../../../services/analyticsService';
import './FailuresByFamilyTab.css';

const DIMENSIONS: FailureDimension[] = ['COMPONENT', 'SYMPTOM', 'CAUSE', 'ACTION'];
const FIRST_YEAR = 2021;
const TOP_HIGHLIGHT = 3;
const RANKING_SIZE = 8;
const OUTCOME_ORDER = ['FACTS', 'NO_FAILURE', 'EMPTY_RESPONSE', 'CALL_FAILED', 'NO_TEXT', 'UNTRACED'];

// Sentinels for the two "no value" buckets, kept apart from any real family/code name.
const UNIDENTIFIED = '__unidentified__';
const NOT_ATTRIBUTED = '__not_attributed__';

const cellKey = (family: string | null, code: string | null): string =>
  `${family ?? UNIDENTIFIED}|${code ?? NOT_ATTRIBUTED}`;

/** Failures per served machine; null (shown "n.d.", never zero) when there is no denominator. */
const failureRate = (f: { facts: number; servedMachines: number | null }): number | null =>
  f.servedMachines ? f.facts / f.servedMachines : null;

const isLowBase = (servedMachines: number | null, min: number): boolean =>
  servedMachines !== null && servedMachines > 0 && servedMachines < min;

interface CodeColumn {
  code: string;
  label: string;
  total: number;
}

const buildColumns = (matrix: FailureMatrixResponse, italian: boolean): CodeColumn[] => {
  const columns = new Map<string, CodeColumn>();
  for (const cell of matrix.cells) {
    if (cell.code === null) continue;
    const label = (italian ? cell.labelIt : cell.labelEn) ?? cell.code;
    const current = columns.get(cell.code);
    columns.set(cell.code, { code: cell.code, label, total: (current?.total ?? 0) + cell.occurrences });
  }
  return Array.from(columns.values()).sort((a, b) => b.total - a.total);
};

export const FailuresByFamilyTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const italian = i18n.language.startsWith('it');
  const [dimension, setDimension] = useState<FailureDimension>('COMPONENT');
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);

  const { matrix, loading, error } = useFailureMatrix(dimension);
  // The component ranking is always about components, whichever facet the matrix is showing.
  const { matrix: componentMatrix } = useFailureMatrix('COMPONENT');
  const { coverage } = useFailureCoverage();
  const { detail, loading: loadingDetail, error: detailError } = useFailureFamilyDetail(selectedFamily);

  const currentYear = new Date().getFullYear();
  const numberFormatter = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const rateFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [i18n.language]
  );
  const pctFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 0, maximumFractionDigits: 1 }),
    [i18n.language]
  );

  const view = useMemo(() => {
    if (!matrix) return null;
    const columns = buildColumns(matrix, italian);
    const values = new Map<string, number>();
    for (const cell of matrix.cells) {
      const key = cellKey(cell.family, cell.code);
      values.set(key, (values.get(key) ?? 0) + cell.occurrences);
    }
    const identified = matrix.families.filter((f) => f.family !== null);
    const unidentified = matrix.families.find((f) => f.family === null);
    const rows: FailureFamilyTotal[] = unidentified ? [...identified, unidentified] : identified;
    const columnTotals = new Map<string, number>(columns.map((c) => [c.code, c.total]));
    const eligible = identified.filter((f) => (f.servedMachines ?? 0) > 0);
    const byRate = [...eligible]
      .sort((a, b) => (failureRate(b) as number) - (failureRate(a) as number))
      .slice(0, RANKING_SIZE);
    const byVolume = [...eligible].sort((a, b) => b.facts - a.facts).slice(0, RANKING_SIZE);
    // A family with a reduced base can top the rate ranking by chance (1 failure on 1 machine): the sentence that
    // compares the two rankings only names as "the family that fails the most" one with a reliable base.
    const reliable = eligible.filter((f) => (f.servedMachines ?? 0) >= matrix.minMachinesForRate);
    const rateLeader = [...(reliable.length > 0 ? reliable : eligible)].sort(
      (a, b) => (failureRate(b) as number) - (failureRate(a) as number)
    )[0];
    return { columns, values, rows, columnTotals, byRate, byVolume, rateLeader };
  }, [matrix, italian]);

  const componentRanking = useMemo(() => {
    if (!componentMatrix) return null;
    const byCode = new Map<string, { code: string; label: string; total: number; families: Set<string> }>();
    for (const cell of componentMatrix.cells) {
      if (cell.code === null) continue;
      const entry = byCode.get(cell.code) ?? {
        code: cell.code,
        label: (italian ? cell.labelIt : cell.labelEn) ?? cell.code,
        total: 0,
        families: new Set<string>(),
      };
      entry.total += cell.occurrences;
      if (cell.family !== null) entry.families.add(cell.family);
      byCode.set(cell.code, entry);
    }
    return Array.from(byCode.values()).sort((a, b) => b.total - a.total);
  }, [componentMatrix, italian]);

  const facetLabel = t(`analytics.failuresByFamily.dimensions.${dimension}`);
  const notAvailable = t('analytics.failuresByFamily.notAvailable');

  const formatRate = (f: { facts: number; servedMachines: number | null }): string => {
    const r = failureRate(f);
    return r === null ? notAvailable : rateFormatter.format(r);
  };

  /** A percentage on a base under the agreed threshold is never computed. */
  const formatShare = (part: number, whole: number, min: number): string =>
    whole >= min && whole > 0 ? `${pctFormatter.format((part / whole) * 100)}%` : t('analytics.failuresByFamily.insufficient');

  const familyName = (family: string | null): string =>
    family === null ? t('analytics.failuresByFamily.matrix.unidentified') : family;

  const topCodes = (family: string | null): Set<string> => {
    if (!view) return new Set();
    return new Set(
      view.columns
        .map((c, index) => ({ code: c.code, value: view.values.get(cellKey(family, c.code)) ?? 0, index }))
        .filter((o) => o.value > 1) // a single occurrence never signals a primacy
        .sort((a, b) => b.value - a.value || a.index - b.index)
        .slice(0, TOP_HIGHLIGHT)
        .map((o) => o.code)
    );
  };

  const toggleFamily = (family: string) => setSelectedFamily((current) => (current === family ? null : family));

  const familyButton = (family: string, low: boolean) => (
    <button
      type="button"
      className={`analytics-fail-link${selectedFamily === family ? ' active' : ''}`}
      onClick={() => toggleFamily(family)}
      title={t('analytics.failuresByFamily.familyCard.open')}
    >
      {family}
      {low && <span className="analytics-fail-flag">{t('analytics.failuresByFamily.matrix.lowBase')}</span>}
    </button>
  );

  const totals = matrix?.totals;
  const minMachines = matrix?.minMachinesForRate ?? 15;
  const identifiedFacts = totals ? totals.facts - totals.unidentifiedFacts : 0;
  const overallRate = totals ? failureRate({ facts: identifiedFacts, servedMachines: totals.servedMachines }) : null;
  const servedWithFamily = totals ? totals.servedMachines : 0;
  const servedTotal = totals ? totals.servedMachines + totals.unidentifiedServedMachines : 0;

  const yearBars = useMemo(() => {
    if (!detail) return [];
    const byYear = new Map(detail.byYear.map((y) => [y.year, y.facts]));
    const years: number[] = [];
    for (let y = FIRST_YEAR; y <= currentYear; y++) years.push(y);
    const max = Math.max(1, ...years.map((y) => byYear.get(y) ?? 0));
    return years.map((y) => ({ year: y, facts: byYear.get(y) ?? 0, height: ((byYear.get(y) ?? 0) / max) * 100 }));
  }, [detail, currentYear]);

  const familyComponents = useMemo(() => {
    if (!componentMatrix || !selectedFamily) return [];
    return componentMatrix.cells
      .filter((c) => c.family === selectedFamily)
      .sort((a, b) => b.occurrences - a.occurrences)
      .map((c) => ({
        key: c.code ?? NOT_ATTRIBUTED,
        label: c.code === null ? t('analytics.failuresByFamily.matrix.notAttributed') : ((italian ? c.labelIt : c.labelEn) ?? c.code),
        occurrences: c.occurrences,
      }));
  }, [componentMatrix, selectedFamily, italian, t]);

  return (
    <div className="analytics-fail">
      <div className="analytics-fail-controls">
        <div className="analytics-fail-control">
          <span className="analytics-fail-control-label">{t('analytics.failuresByFamily.period.label')}</span>
          <span className="analytics-fail-static">
            {t('analytics.failuresByFamily.period.value', { from: FIRST_YEAR, to: currentYear })}
          </span>
        </div>
        <div className="analytics-fail-control">
          <span className="analytics-fail-control-label">{t('analytics.failuresByFamily.facet')}</span>
          <div className="analytics-heatmap-dimension-selector" role="tablist">
            {DIMENSIONS.map((d) => (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={dimension === d}
                className={`analytics-heatmap-dimension-btn${dimension === d ? ' active' : ''}`}
                onClick={() => setDimension(d)}
              >
                {t(`analytics.failuresByFamily.dimensions.${d}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <CollapsibleNote title={t('analytics.failuresByFamily.notes.period')}>
        {t('analytics.failuresByFamily.period.note')}
      </CollapsibleNote>

      {loading && <p className="analytics-coming-soon">{t('app.loading')}</p>}
      {!loading && (error || !matrix || !view || !totals) && <p className="analytics-error">{t('analytics.loadError')}</p>}
      {!loading && matrix && totals && matrix.totals.facts === 0 && (
        <p className="analytics-coming-soon">{t('analytics.failuresByFamily.noData')}</p>
      )}

      {!loading && matrix && view && totals && totals.facts > 0 && (
        <>
          <div className="analytics-kpi-cards">
            <div className="analytics-kpi-card">
              <div className="analytics-kpi-label">{t('analytics.failuresByFamily.kpis.facts')}</div>
              <div className="analytics-kpi-value">{numberFormatter.format(totals.facts)}</div>
              <div className="analytics-fail-kpi-note">
                {t('analytics.failuresByFamily.kpis.factsNote', { rows: numberFormatter.format(totals.failureRows) })}
              </div>
            </div>
            <div className="analytics-kpi-card">
              <div className="analytics-kpi-label">{t('analytics.failuresByFamily.kpis.machines')}</div>
              <div className="analytics-kpi-value">{numberFormatter.format(totals.servedMachines)}</div>
              <div className="analytics-fail-kpi-note">{t('analytics.failuresByFamily.kpis.machinesNote')}</div>
            </div>
            <div className="analytics-kpi-card">
              <div className="analytics-kpi-label">{t('analytics.failuresByFamily.kpis.rate')}</div>
              <div className="analytics-kpi-value">{overallRate === null ? notAvailable : rateFormatter.format(overallRate)}</div>
              <div className="analytics-fail-kpi-note">{t('analytics.failuresByFamily.kpis.rateNote')}</div>
            </div>
            <div className="analytics-kpi-card">
              <div className="analytics-kpi-label">{t('analytics.failuresByFamily.kpis.coverage')}</div>
              <div className="analytics-kpi-value">{formatShare(servedWithFamily, servedTotal, minMachines)}</div>
              <div className="analytics-fail-kpi-note">{t('analytics.failuresByFamily.kpis.coverageNote')}</div>
            </div>
          </div>

          <CollapsibleNote variant="warn" title={t('analytics.failuresByFamily.definition.title')}>
            <p>
              {t('analytics.failuresByFamily.definition.body', {
                rows: numberFormatter.format(totals.failureRows),
                facts: numberFormatter.format(totals.facts),
              })}
            </p>
            {matrix.byServiceType.length > 0 && (
              <p className="analytics-fail-service-types">
                <span>{t('analytics.failuresByFamily.definition.serviceTypes')}: </span>
                {matrix.byServiceType.map((s, index) => (
                  <span key={s.serviceType}>
                    {index > 0 && ' · '}
                    {s.serviceType === 'unknown'
                      ? t('analytics.notClassified')
                      : t(`analytics.serviceTypes.${s.serviceType}`, { defaultValue: s.serviceType })}{' '}
                    {formatShare(s.failureRows, totals.failureRows, minMachines)}
                  </span>
                ))}
              </p>
            )}
            <p className="analytics-fail-multi">{t('analytics.failuresByFamily.definition.multi')}</p>
          </CollapsibleNote>

          <CollapsibleNote title={t('analytics.failuresByFamily.axisCoverage.title', { facet: facetLabel })}>
            <p>
              {t('analytics.failuresByFamily.axisCoverage.body', {
                coded: numberFormatter.format(totals.facts - totals.unattributedFacts),
                total: numberFormatter.format(totals.facts),
                pct: formatShare(totals.facts - totals.unattributedFacts, totals.facts, minMachines),
                withText: numberFormatter.format(totals.unattributedWithText),
                without: numberFormatter.format(totals.unattributedWithoutText),
              })}
            </p>
            {coverage && coverage.closedRows > 0 && (
              <p className="analytics-fail-processing">
                <span>{t('analytics.failuresByFamily.processing.title', { closed: numberFormatter.format(coverage.closedRows) })}: </span>
                {OUTCOME_ORDER.map((outcome) => ({ outcome, rows: coverage.byOutcome.find((o) => o.outcome === outcome)?.rows ?? 0 }))
                  .filter((o) => o.rows > 0)
                  .map((o, index) => (
                    <span key={o.outcome}>
                      {index > 0 && ' · '}
                      {t(`analytics.failuresByFamily.processing.outcomes.${o.outcome}`)} {numberFormatter.format(o.rows)}
                    </span>
                  ))}
                {coverage.byOutcome.some((o) => o.outcome === 'UNTRACED' && o.rows > 0) && (
                  <>
                    <br />
                    {t('analytics.failuresByFamily.processing.untracedNote')}
                  </>
                )}
              </p>
            )}
          </CollapsibleNote>

          <div className="analytics-panel">
            <h3>{t('analytics.failuresByFamily.matrix.title', { facet: facetLabel })}</h3>
            <CollapsibleNote title={t('analytics.failuresByFamily.notes.matrix')}>
              <p>{t('analytics.failuresByFamily.matrix.sub', { top: TOP_HIGHLIGHT })}</p>
              <p>{t('analytics.failuresByFamily.matrix.denominatorNote', { min: minMachines })}</p>
              {totals.unidentifiedFacts > 0 && (
                <p>
                  {t('analytics.failuresByFamily.matrix.unidentifiedNote', {
                    facts: numberFormatter.format(totals.unidentifiedFacts),
                    machines: numberFormatter.format(totals.unidentifiedServedMachines),
                  })}
                </p>
              )}
            </CollapsibleNote>
            <div className="analytics-heatmap-container">
              <table className="analytics-heatmap-table">
                <thead>
                  <tr>
                    <th className="analytics-heatmap-corner">{t('analytics.failuresByFamily.familyColumn')}</th>
                    {view.columns.map((c) => (
                      <th key={c.code} title={c.label}>
                        {/* zero-width space after "/": the only place a long label ("Registration guides/registers") may wrap */}
                        {c.label.replace(/\//g, '/\u200b')}
                      </th>
                    ))}
                    <th className="analytics-fail-not-attributed">{t('analytics.failuresByFamily.matrix.notAttributed')}</th>
                    <th className="analytics-heatmap-total-col">{t('analytics.failuresByFamily.totalColumn')}</th>
                    <th>{t('analytics.failuresByFamily.matrix.machines')}</th>
                    <th>{t('analytics.failuresByFamily.matrix.rate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.rows.map((row) => {
                    const top = topCodes(row.family);
                    const unidentified = row.family === null;
                    const low = isLowBase(row.servedMachines, minMachines);
                    return (
                      <tr key={row.family ?? UNIDENTIFIED} className={unidentified ? 'analytics-heatmap-row-unclassified' : undefined}>
                        <th scope="row">{unidentified ? familyName(null) : familyButton(row.family as string, false)}</th>
                        {view.columns.map((c) => {
                          const value = view.values.get(cellKey(row.family, c.code)) ?? 0;
                          return (
                            <td
                              key={c.code}
                              className={`analytics-fail-cell${value === 0 ? ' zero' : top.has(c.code) ? ' top' : ''}`}
                              title={`${familyName(row.family)} — ${c.label}: ${value}`}
                            >
                              {value === 0 ? '·' : numberFormatter.format(value)}
                            </td>
                          );
                        })}
                        <td className="analytics-fail-cell analytics-fail-not-attributed">
                          {(view.values.get(cellKey(row.family, null)) ?? 0) === 0
                            ? '·'
                            : numberFormatter.format(view.values.get(cellKey(row.family, null)) ?? 0)}
                        </td>
                        <td className="analytics-heatmap-total-col">{numberFormatter.format(row.facts)}</td>
                        <td className="analytics-fail-cell">
                          {row.servedMachines === null ? notAvailable : numberFormatter.format(row.servedMachines)}
                        </td>
                        <td className={`analytics-fail-cell${low ? ' muted' : ''}`}>
                          {formatRate(row)}
                          {low && <span className="analytics-fail-flag">{t('analytics.failuresByFamily.matrix.lowBase')}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{t('analytics.failuresByFamily.totalColumn')}</td>
                    {view.columns.map((c) => (
                      <td key={c.code}>{numberFormatter.format(view.columnTotals.get(c.code) ?? 0)}</td>
                    ))}
                    <td className="analytics-fail-not-attributed">{numberFormatter.format(totals.unattributedFacts)}</td>
                    <td className="analytics-heatmap-total-col">{numberFormatter.format(totals.facts)}</td>
                    <td>{numberFormatter.format(totals.servedMachines)}</td>
                    <td>{overallRate === null ? notAvailable : rateFormatter.format(overallRate)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="analytics-panel">
            <h3>{t('analytics.failuresByFamily.rankings.title')}</h3>
            <CollapsibleNote title={t('analytics.failuresByFamily.notes.rankings')}>
              {t('analytics.failuresByFamily.rankings.sub')}
            </CollapsibleNote>
            <div className="analytics-fail-grid2">
              {(['rate', 'volume'] as const).map((kind) => {
                const data = kind === 'rate' ? view.byRate : view.byVolume;
                const metric = (f: FailureFamilyTotal): number => (kind === 'rate' ? (failureRate(f) as number) : f.facts);
                const max = Math.max(...data.map(metric), 0);
                return (
                  <div key={kind}>
                    <h4>{t(`analytics.failuresByFamily.rankings.${kind}Title`)}</h4>
                    <table className="analytics-fail-table">
                      <thead>
                        <tr>
                          <th>{t('analytics.failuresByFamily.familyColumn')}</th>
                          <th>{t('analytics.failuresByFamily.rankings.machines')}</th>
                          <th>{t('analytics.failuresByFamily.rankings.failures')}</th>
                          <th>{t(`analytics.failuresByFamily.rankings.${kind}Metric`)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((f) => {
                          const low = isLowBase(f.servedMachines, minMachines);
                          return (
                            <tr key={f.family as string}>
                              <td>{familyButton(f.family as string, low)}</td>
                              <td className="muted">{numberFormatter.format(f.servedMachines ?? 0)}</td>
                              <td>{numberFormatter.format(f.facts)}</td>
                              <td>
                                <div className="analytics-fail-bar">
                                  <div className="analytics-fail-bar-track">
                                    <div
                                      className={`analytics-fail-bar-fill${kind === 'rate' && low ? ' muted' : ''}`}
                                      style={{ width: `${max > 0 ? (metric(f) / max) * 100 : 0}%` }}
                                    />
                                  </div>
                                  <span className={kind === 'rate' && low ? 'muted' : undefined}>
                                    {kind === 'rate' ? formatRate(f) : numberFormatter.format(f.facts)}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
            {view.rateLeader && view.byVolume[0] && (
              <p className="analytics-fail-note">
                {view.rateLeader.family !== view.byVolume[0].family
                  ? t('analytics.failuresByFamily.rankings.differ', {
                      volumeFamily: view.byVolume[0].family,
                      volumeFacts: numberFormatter.format(view.byVolume[0].facts),
                      volumeMachines: numberFormatter.format(view.byVolume[0].servedMachines ?? 0),
                      volumeRate: formatRate(view.byVolume[0]),
                      rateFamily: view.rateLeader.family,
                      rateFacts: numberFormatter.format(view.rateLeader.facts),
                      rateMachines: numberFormatter.format(view.rateLeader.servedMachines ?? 0),
                      rateRate: formatRate(view.rateLeader),
                    })
                  : t('analytics.failuresByFamily.rankings.same')}
              </p>
            )}
          </div>

          {componentRanking && componentMatrix && (
            <div className="analytics-panel">
              <h3>{t('analytics.failuresByFamily.components.title')}</h3>
              <CollapsibleNote title={t('analytics.failuresByFamily.notes.components')}>
                {t('analytics.failuresByFamily.components.sub')}
              </CollapsibleNote>
              <table className="analytics-fail-table">
                <thead>
                  <tr>
                    <th>{t('analytics.failuresByFamily.components.component')}</th>
                    <th>{t('analytics.failuresByFamily.components.failures')}</th>
                    <th>{t('analytics.failuresByFamily.components.families')}</th>
                    <th>{t('analytics.failuresByFamily.components.share')}</th>
                  </tr>
                </thead>
                <tbody>
                  {componentRanking.map((c) => (
                    <tr key={c.code}>
                      <td>{c.label}</td>
                      <td>{numberFormatter.format(c.total)}</td>
                      <td className="muted">{c.families.size}</td>
                      <td>{formatShare(c.total, componentMatrix.totals.facts, minMachines)}</td>
                    </tr>
                  ))}
                  {componentMatrix.totals.unattributedFacts > 0 && (
                    <tr className="analytics-fail-not-attributed-row">
                      <td>{t('analytics.failuresByFamily.matrix.notAttributed')}</td>
                      <td>{numberFormatter.format(componentMatrix.totals.unattributedFacts)}</td>
                      <td className="muted">{t('analytics.failuresByFamily.notAvailable')}</td>
                      <td>{formatShare(componentMatrix.totals.unattributedFacts, componentMatrix.totals.facts, minMachines)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {selectedFamily && (
            <div className="analytics-panel">
              <div className="analytics-fail-card-head">
                <h3>{t('analytics.failuresByFamily.familyCard.title', { family: selectedFamily })}</h3>
                <button type="button" className="analytics-fail-close" onClick={() => setSelectedFamily(null)}>
                  {t('analytics.failuresByFamily.familyCard.close')}
                </button>
              </div>
              {loadingDetail && <p className="analytics-coming-soon">{t('app.loading')}</p>}
              {!loadingDetail && (detailError || !detail) && <p className="analytics-error">{t('analytics.loadError')}</p>}
              {!loadingDetail && detail && (
                <>
                  <CollapsibleNote title={t('analytics.failuresByFamily.notes.familyCard')}>
                    {t('analytics.failuresByFamily.familyCard.hint')}
                  </CollapsibleNote>
                  <div className="analytics-fail-grid2">
                    <div>
                      <h4>{t('analytics.failuresByFamily.familyCard.byYear')}</h4>
                      <div className="analytics-fail-spark">
                        {yearBars.map((y) => (
                          <div key={y.year} className="analytics-fail-spark-bar" style={{ height: `${Math.max(y.height, 2)}%` }}>
                            <span>{y.facts}</span>
                          </div>
                        ))}
                      </div>
                      <div className="analytics-fail-spark-x">
                        {yearBars.map((y) => (
                          <div key={y.year}>{y.year}</div>
                        ))}
                      </div>
                      <p className="analytics-fail-note">
                        {t('analytics.failuresByFamily.familyCard.summary', {
                          machines: numberFormatter.format(detail.servedMachines),
                          facts: numberFormatter.format(detail.facts),
                          rate: formatRate({ facts: detail.facts, servedMachines: detail.servedMachines }),
                          year: currentYear,
                        })}
                        {isLowBase(detail.servedMachines, minMachines) && (
                          <span className="analytics-fail-flag">{t('analytics.failuresByFamily.matrix.lowBase')}</span>
                        )}
                      </p>
                      <h4>{t('analytics.failuresByFamily.familyCard.components')}</h4>
                      <table className="analytics-fail-table">
                        <tbody>
                          {familyComponents.slice(0, 6).map((c) => (
                            <tr key={c.key}>
                              <td>{c.label}</td>
                              <td>{numberFormatter.format(c.occurrences)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <h4>{t('analytics.failuresByFamily.familyCard.spareParts')}</h4>
                      {detail.topSpareParts.length === 0 ? (
                        <p className="analytics-coming-soon">{t('analytics.failuresByFamily.familyCard.noSpareParts')}</p>
                      ) : (
                        <table className="analytics-fail-table">
                          <thead>
                            <tr>
                              <th>{t('analytics.failuresByFamily.familyCard.product')}</th>
                              <th>{t('analytics.failuresByFamily.familyCard.description')}</th>
                              <th>{t('analytics.failuresByFamily.familyCard.used')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.topSpareParts.map((p) => (
                              <tr key={p.productCode}>
                                <td>{p.productCode}</td>
                                <td className="muted">{(italian ? p.descriptionIt : p.descriptionEn) ?? p.descriptionIt ?? p.descriptionEn ?? '—'}</td>
                                <td>{numberFormatter.format(p.used)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FailuresByFamilyTab;
